/**
 * Thin fetch wrapper. The session lives in an httpOnly cookie, so no token handling
 * here; every mutating request carries X-Requested-With for the API's CSRF check.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fieldErrors: { path: string; message: string }[] = [],
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query) {
  const url = new URL(`/api${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  return url.toString();
}

let onUnauthorized: (() => void) | undefined;
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const isForm = body instanceof FormData;
  const res = await fetch(buildUrl(path, query), {
    method,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'fetch',
      ...(body && !isForm ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/')) onUnauthorized?.();
    const message = Array.isArray(data.message) ? data.message.join(', ') : data.message;
    throw new ApiError(res.status, message ?? 'Ndodhi një gabim i papritur.', data.errors);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

export const mediaUrl = (id: string) => `/api/media/${id}`;
/** Small JPEG preview (photo thumbnail or video poster frame). */
export const thumbUrl = (id: string) => `/api/media/${id}/thumb`;
/** Same file, saved by the browser instead of opened. */
export const downloadUrl = (id: string) => `/api/media/${id}?download=1`;

/** Upload with progress reporting (fetch cannot report upload progress). */
export function uploadFile<T>(file: File, onProgress?: (pct: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/media');
    xhr.setRequestHeader('X-Requested-With', 'fetch');
    xhr.upload.onprogress = (e) =>
      e.lengthComputable && onProgress?.(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      const data = JSON.parse(xhr.responseText || '{}');
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, data.message ?? 'Ngarkimi dështoi.'));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Lidhja me serverin dështoi.'));
    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}
